import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitTo,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore'
import type { Auth } from 'firebase/auth'
import type {
  Notification,
  NotificationChoice,
  NotificationSettings,
  NotificationType,
} from '@domain/notification'
import type { NotificationRepository } from '@domain/ports/finance-repository'
import { optionalText, requireIso, requireOneOf, requireText, textOr, toIso } from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * A central de notificações.
 *
 * ## Esta consulta MERECE índice composto, ao contrário das três anteriores
 *
 * `invoices`, `breakdownItems` e `comments` viraram igualdade num campo só com
 * ordenação em memória, porque o conjunto que volta é minúsculo e delimitado —
 * uma fatura por mês, poucos itens por transação, poucos comentários por
 * entidade. Aqui não: `notifications` **cresce sem teto**, uma por evento por
 * membro, para sempre. Ler tudo para mostrar as vinte mais recentes seria pior a
 * cada semana.
 *
 * Então a consulta é `recipientUserId ==` mais `orderBy('createdAt','desc')`
 * mais `limit` — dois campos distintos, índice composto declarado em
 * `firestore.indexes.json`. **Índice que falta não falha no emulador**: falha em
 * produção, com `FAILED_PRECONDITION`, na primeira vez que alguém abre a
 * central. É por isso que `firestore-indexes.test.ts` deriva o índice desta
 * consulta e o compara com o arquivo.
 *
 * ## Só as minhas
 *
 * `firestore.rules` já nega ler a notificação de outra pessoa — o filtro por
 * `recipientUserId` não é segurança, é a consulta certa. Sem ele a regra
 * recusaria a leitura inteira, porque uma consulta só passa se **todo**
 * documento que ela pode devolver for permitido.
 *
 * ## `readAt` é o único campo que o cliente escreve
 *
 * A regra cobra com `affectedKeys().hasOnly(['readAt'])`, desde a Fase 1. Esta
 * porta não oferece mais nada — o resto é do `NotificationService`.
 */

const TYPES: readonly NotificationType[] = [
  'transaction.created',
  'transaction.large_amount',
  'invoice.closed',
  'invoice.due_soon',
  'receipt.awaiting_review',
  'product.price_drop',
  'reminder.due',
  'sync.failed',
  'sync.summary',
]
const CHOICES = ['push', 'inApp', 'off'] as const
const DELIVERY = ['pending', 'sent', 'failed', 'skipped'] as const

/**
 * **As listas acima são de runtime.** Um tipo que exista na união e falte aqui
 * faz **toda notificação daquele tipo lançar na leitura** — e a central inteira
 * some por causa de um documento. As linhas abaixo só compilam se os conjuntos
 * forem iguais.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _tiposCobremAUniao: Equals<NotificationType, (typeof TYPES)[number]> = true
const _escolhasCobremAUniao: Equals<NotificationChoice, (typeof CHOICES)[number]> = true
void _tiposCobremAUniao
void _escolhasCobremAUniao

function notificationsPath(): string {
  return `households/${HOUSEHOLD_ID}/notifications`
}

/**
 * O endpoint da inscrição virado id de documento.
 *
 * SHA-256 em hex: o endpoint é uma URL longa, com `/` e `.`, e nenhum dos dois
 * cabe num id do Firestore. O hash é **estável** — é isso que faz reassinar no
 * mesmo aparelho substituir o documento em vez de criar outro.
 *
 * Não é segurança: o endpoint não é segredo, e o documento guarda ele inteiro
 * logo ao lado. É só um id determinístico.
 */
async function enderecoComoId(endpoint: string): Promise<string> {
  const bytes = new TextEncoder().encode(endpoint)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

function settingsPath(uid: string): string {
  return `households/${HOUSEHOLD_ID}/members/${uid}/notificationSettings`
}

function toDelivery(value: unknown): Notification['delivery']['inApp'] {
  const bruto = (value ?? {}) as Record<string, unknown>
  return {
    // Ausente vira `pending`, e não erro: um documento de forma antiga não pode
    // derrubar a central. `pending` é o estado que não afirma nada.
    status: DELIVERY.find((s) => s === bruto['status']) ?? 'pending',
    attemptedAt: toIso(bruto['attemptedAt']),
    error: optionalText(bruto['error']),
  }
}

export function toNotification(id: string, data: DocumentData): Notification {
  const where = `notifications/${id}`
  const entityRef = data['entityRef'] as Record<string, unknown> | null | undefined
  const delivery = (data['delivery'] ?? {}) as Record<string, unknown>
  const dados = (data['data'] ?? {}) as Record<string, unknown>

  return {
    id,
    householdId: HOUSEHOLD_ID,
    recipientUserId: requireText(data['recipientUserId'], `${where}.recipientUserId`),
    type: requireOneOf(data['type'], TYPES, `${where}.type`),
    // Título e corpo caem num default: uma notificação sem texto é feia, não
    // errada, e derrubar a central por causa dela seria pior.
    title: textOr(data['title'], ''),
    body: textOr(data['body'], ''),
    entityRef:
      entityRef === null || entityRef === undefined
        ? null
        : {
            type: requireText(entityRef['type'], `${where}.entityRef.type`) as NonNullable<
              Notification['entityRef']
            >['type'],
            id: requireText(entityRef['id'], `${where}.entityRef.id`),
          },
    data: Object.fromEntries(
      Object.entries(dados).map(([k, v]) => [k, typeof v === 'string' ? v : String(v)]),
    ),
    readAt: toIso(data['readAt']),
    delivery: {
      webPush: toDelivery(delivery['webPush']),
      inApp: toDelivery(delivery['inApp']),
    },
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
  }
}

function minhasQuery(db: Firestore, uid: string): Query {
  return query(
    collection(db, notificationsPath()),
    where('recipientUserId', '==', uid),
    orderBy('createdAt', 'desc'),
    limitTo(50),
  )
}

/** O registro que `firestore-indexes.test.ts` varre. Ver `INVOICE_QUERIES`. */
export const NOTIFICATION_QUERIES: Record<string, (db: Firestore, uid: string) => Query> = {
  listMine: minhasQuery,
}

export function createFirebaseNotificationRepository(
  db: Firestore,
  auth: Auth,
): NotificationRepository {
  const uid = (): string => {
    const current = auth.currentUser?.uid
    if (current === undefined) {
      throw new Error('notification-repository: não há sessão.')
    }
    return current
  }

  return {
    listMine: async () => {
      const snap = await getDocs(minhasQuery(db, uid()))
      return snap.docs.map((n) => toNotification(n.id, n.data()))
    },

    markRead: async (id) => {
      // Só `readAt`. A regra recusa qualquer outra chave no mesmo update.
      await updateDoc(doc(db, notificationsPath(), id), { readAt: serverTimestamp() })
    },

    markAllRead: async (ids) => {
      // **Um lote, e não N escritas soltas.** Marcar dez como lidas de uma vez
      // é uma ação só do ponto de vista de quem clica; dez round-trips fariam o
      // contador descer aos poucos na tela.
      if (ids.length === 0) return
      const lote = writeBatch(db)
      for (const id of ids) {
        lote.update(doc(db, notificationsPath(), id), { readAt: serverTimestamp() })
      }
      await lote.commit()
    },

    removeAll: async (ids) => {
      // Mesmo lote, mesma razão de `markAllRead` — e aqui ela pesa mais: dez
      // linhas sumindo uma a uma da lista é a tela se desmontando na frente de
      // quem clicou.
      //
      // **500 é o teto de um lote do Firestore.** A central guarda dezenas, não
      // milhares, mas o corte existe para o dia em que guardar: sem ele, a
      // escrita falharia inteira em vez de ir em duas.
      for (let i = 0; i < ids.length; i += 500) {
        const lote = writeBatch(db)
        for (const id of ids.slice(i, i + 500)) {
          lote.delete(doc(db, notificationsPath(), id))
        }
        await lote.commit()
      }
    },

    readSettings: async () => {
      const snap = await getDoc(doc(db, settingsPath(uid()), 'preferences'))
      if (!snap.exists()) return null
      const bruto = (snap.get('byType') ?? {}) as Record<string, unknown>
      const byType: Partial<Record<NotificationType, NotificationChoice>> = {}
      for (const [tipo, escolha] of Object.entries(bruto)) {
        // Valor desconhecido é descartado e vale o padrão do tipo — que erra
        // para "a pessoa recebe", que é o lado seguro.
        const conhecido = CHOICES.find((c) => c === escolha)
        if (conhecido !== undefined && TYPES.includes(tipo as NotificationType)) {
          byType[tipo as NotificationType] = conhecido
        }
      }
      return { byType }
    },

    saveSettings: async (settings: NotificationSettings) => {
      await setDoc(doc(db, settingsPath(uid()), 'preferences'), { byType: settings.byType })
    },

    savePushSubscription: async (subscription) => {
      // **O id vem do endpoint**, e não é gerado: reassinar no mesmo aparelho
      // tem de substituir, não acumular. O endpoint é uma URL — vira id por um
      // hash estável, porque `/` e `.` não podem entrar num id de documento.
      const id = await enderecoComoId(subscription.endpoint)
      await setDoc(doc(db, `${settingsPath(uid()).replace('notificationSettings', 'pushSubscriptions')}`, id), {
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        createdAt: serverTimestamp(),
      })
    },
  }
}
